import { TEAMS } from '../constants/roles';
import { buildScopedTeamOptions, DEFAULT_DEPARTMENT } from '../constants/departments';

/** Collapse legacy slugs (mobile_app) and labels for dedup / filter matching. */
export function canonicalTeamKey(team) {
  if (!team || typeof team !== 'string') return '';
  return team
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map raw assignee team to the canonical TEAMS value when possible. */
export function resolveTeamDisplayName(team, teamMeta = TEAMS) {
  const raw = (team || '').trim();
  if (!raw) return '';
  const key = canonicalTeamKey(raw);
  const fromMeta = teamMeta.find(
    (t) =>
      t.value !== 'Any'
      && (canonicalTeamKey(t.value) === key || canonicalTeamKey(t.label || '') === key),
  );
  if (fromMeta) return fromMeta.value;
  return key.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function teamsMatch(teamA, teamB) {
  const a = canonicalTeamKey(teamA);
  const b = canonicalTeamKey(teamB);
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a === b;
}

/** Normalize GET /projects/:id/members (array or wrapped payload). */
export function normalizeProjectMembersResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.members)) return data.members;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
}

/** Static TEAMS plus custom config teams (legacy — prefer mergeTeamsForDepartment). */
export function mergeTeamsWithCustom(customTeams = []) {
  return [
    ...TEAMS,
    ...(customTeams || [])
      .filter((ct) => ct?.value && !TEAMS.some((t) => t.value === ct.value))
      .map((ct) => ({
        value: ct.value,
        label: ct.label || ct.value,
        ...(ct.role ? { role: ct.role } : {}),
      })),
  ];
}

/** Assign-to-team dropdown scoped to a department (invite / user-mgmt parity) + Any. */
export function mergeTeamsForDepartment(customTeams = [], department = DEFAULT_DEPARTMENT) {
  const scoped = buildScopedTeamOptions(department, customTeams);
  return [{ value: 'Any', label: 'Any' }, ...scoped];
}

/**
 * When team is empty or "Any", return all base users; otherwise filter by
 * `user.team` or by TEAMS/custom meta `role` when the user has no team set.
 */
export function filterAssignableUsersByTeam(baseAssignableUsers, teamValue, allTeamsMeta) {
  const sel = (teamValue || '').trim();
  if (!sel || sel === 'Any') {
    return baseAssignableUsers;
  }
  const selectedTeamMeta = (allTeamsMeta || []).find((t) => t.value === sel);
  return baseAssignableUsers.filter((u) => {
    if (u.team) return (u.team || '').toLowerCase() === sel.toLowerCase();
    return selectedTeamMeta?.role && u.role?.toLowerCase() === String(selectedTeamMeta.role).toLowerCase();
  });
}
